import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { toFullName } from '@repo/toolkit/to-full-name'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import { DownArrowIcon, SplitOneIcon, XIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation } from '@tanstack/react-query'
import { useMemo } from 'react'
import { toast } from 'sonner'

import { assignSupervisorToEnrollment } from '../../../api/trip-folder/assign-enrollments-to-supervisor'
import { unassignSupervisorFromEnrollment } from '../../../api/trip-folder/unassign-enrollemnts-from-supervisor'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { StaffLight } from '../../../lib/types/models/staff/staff'
import { TripFolderEnrollmentSupervisorFormInput } from '../../../lib/types/models/trip-folder/enrollment-supervisor-input'
import { TripFolderPassengersFilters } from '../../../lib/types/models/trip-folder/trip-folder-passengers'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import AssignSupervisorToEnrollmentForm from './assign-supervisor-to-enrollment-form'

interface SupervisorAssignmentCellProps {
  enrollmentId: number
  supervisor?: StaffLight
  selectedFilters: TripFolderPassengersFilters
  tripFolderId: number
  onSuccess: () => void
}

const SupervisorAssignmentCell = ({
  supervisor,
  enrollmentId,
  selectedFilters,
  tripFolderId,
  onSuccess
}: SupervisorAssignmentCellProps) => {
  const { modalState, toggleModal, closeModel } = useModalManager<'assign-supervisor', number>([
    'assign-supervisor'
  ])

  const { mutateAsync: assignSupervisorToEnrollmentMutation } = useMutation({
    mutationFn: assignSupervisorToEnrollment,
    onSuccess: () => {
      toast.success('Encadrant assigné avec succès')
      closeModel('assign-supervisor')
      onSuccess()
    },
    onError: showAxiosError
  })

  const { mutate: unassignSupervisorFromEnrollmentMutation, isPending } = useMutation({
    mutationFn: unassignSupervisorFromEnrollment,
    onSuccess: () => {
      toast.success('Encadrant retiré avec succès')
      onSuccess()
    },
    onError: showAxiosError
  })

  const handleSubmit = async (input: TripFolderEnrollmentSupervisorFormInput) => {
    const tripBookingId = selectedFilters?.tripBookingIds?.at(0)
    const centerId = selectedFilters?.centerIds?.at(0)

    if (tripBookingId) {
      await assignSupervisorToEnrollmentMutation({
        tripFolderId,
        enrollmentId,
        supervisorId: input.supervisorId,
        tripBookingId
      })
    } else if (centerId) {
      await assignSupervisorToEnrollmentMutation({
        tripFolderId,
        enrollmentId,
        supervisorId: input.supervisorId,
        centerId
      })
    } else {
      toast.error('Veuillez sélectionner un voyage ou un centre')
    }
  }

  const handleUnassign = () => {
    const centerId = selectedFilters?.centerIds?.at(0)
    const tripBookingId = selectedFilters?.tripBookingIds?.at(0)

    if (centerId) {
      unassignSupervisorFromEnrollmentMutation({ tripFolderId, enrollmentId, centerId })
    } else if (tripBookingId) {
      unassignSupervisorFromEnrollmentMutation({ tripFolderId, enrollmentId, tripBookingId })
    } else {
      toast.error('Veuillez sélectionner un voyage ou un centre')
    }
  }

  const handleSplitClick = () => {
    toggleModal('assign-supervisor', enrollmentId)
  }

  const supervisorName = useMemo(
    () => toFullName(supervisor?.firstName, supervisor?.lastName),
    [supervisor]
  )

  return (
    <div className="flex items-center gap-2">
      {supervisorName && (
        <Badge
          variant="warning"
          color={tailwindColors.secondary.light.hover}
          text={
            <div className="flex items-center gap-2">
              <Typography variant="label-medium">{supervisorName}</Typography>
              <Button
                loading={isPending}
                size="xsmall"
                icon={XIcon}
                variant="highlighted"
                className="w-fit h-fit p-0 border-none"
                onClick={handleUnassign}
              />
            </div>
          }
        />
      )}

      <Button
        variant="destructive"
        className={cn('rounded-sm', {
          'pointer-events-none': supervisorName
        })}
        size="xsmall"
        icon={supervisorName ? DownArrowIcon : SplitOneIcon}
        onClick={handleSplitClick}
      />

      <Modal
        isOpen={modalState['assign-supervisor']}
        icon={SplitOneIcon}
        size="medium"
        title="Choisir un encadrant pour faire l'appel de cette partie des jeunes"
        titleAlignment="left"
        onCancel={() => toggleModal('assign-supervisor')}
        hideActions
      >
        <AssignSupervisorToEnrollmentForm
          folderId={tripFolderId}
          className={MODAL_BODY_CLASSNAME}
          buttonsConfig={modalButtonConfig({
            onCancel: () => toggleModal('assign-supervisor'),
            onConfirm: handleSubmit
          })}
        />
      </Modal>
    </div>
  )
}

export default SupervisorAssignmentCell
